import React, {useCallback, useContext, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {HeaderPrefix, IntegrationFilter} from 'src/components/filters';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {AccountSortField} from 'src/openapi/admin/models/AccountSortField';
import {SortDirection, SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {SortHeaderFilter} from '@pb/kit-component-filters';

import {COLUMNS, FILTERING_WARNING, HEADERS, SELECTION_KEY, SORTING_WARNING} from '../../constants';
import {useGetAccountSearchResultCrmIntegration} from '../../hooks/useGetAccountSearchResultCrmIntegration';
import {
    accountSearchResultStateActions,
    accountSearchResultStateSelector,
} from '../../slices/account-search-result-state.slice';
import {accountSearchResultResultSelector} from '../../slices/account-search-result.slice';

import {HeaderSkeleton} from './header-skeleton';

type Props = {
    accountIds: number[],
    totalCount: number,
};

const DealCloudIntegrationController = ({accountIds, totalCount}: Props) => {
    const dispatch = useDispatch();
    const {setErrorMessage} = useContext(MessageContext);
    const cacheKeys = useSelector(accountSearchResultStateSelector);

    const [dealCloudIntegrations, toggleIntegration] = useState(
        cacheKeys.filters.dealCloudIntegrations as boolean | undefined,
    );

    const {isLoading, loadingError} = useGetAccountSearchResultCrmIntegration({
        accountIds,
        totalCount,
        columnId: COLUMNS.DEAL_CLOUD_INTEGRATION,
    });

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            toggleIntegration(cacheKeys.filters.dealCloudIntegrations as boolean | undefined);
        }
    }, cacheKeys.filters.dealCloudIntegrations);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        if (totalCount > MAX_SELECTED_ENTITIES) {
            setErrorMessage(SORTING_WARNING);
            return;
        }

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: AccountSortField.DEAL_CLOUD_INTEGRATION,
        }));
    }, [totalCount, dispatch, setErrorMessage]);

    const applyFilter = useCallback(() => {
        const {dealCloudIntegrations: _prevValue, ...otherFilters} = cacheKeys.filters;
        dispatch(clearSelection(SELECTION_KEY));

        if (dealCloudIntegrations !== undefined) {
            dispatch(accountSearchResultStateActions.setFilters({
                ...otherFilters,
                dealCloudIntegrations,
            }));
        } else {
            dispatch(accountSearchResultStateActions.setFilters(otherFilters));
        }
    }, [dealCloudIntegrations, dispatch, cacheKeys.filters]);

    const clearFilter = useCallback(() => {
        const {dealCloudIntegrations: _prevValue, ...otherFilters} = cacheKeys.filters;
        toggleIntegration(undefined);

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters(otherFilters));
    }, [dispatch, cacheKeys.filters]);

    const cancelFilter = useCallback(() => {
        toggleIntegration(cacheKeys.filters.dealCloudIntegrations as boolean | undefined);
    }, [cacheKeys.filters]);

    return (
        <SortHeaderFilter

            filterActive={cacheKeys.filters?.dealCloudIntegrations !== undefined}
            preventFilterOpening={totalCount > MAX_SELECTED_ENTITIES
                ? () => setErrorMessage(FILTERING_WARNING)
                : undefined}
            sortable={true}
            caption={HEADERS[COLUMNS.DEAL_CLOUD_INTEGRATION]}
            contentBefore={(isLoading || loadingError)
                ? <HeaderPrefix isLoading={isLoading} loadingError={loadingError}/>
                : undefined}
            direction={cacheKeys.sortCriteria.field === AccountSortField.DEAL_CLOUD_INTEGRATION
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <IntegrationFilter value={dealCloudIntegrations} onChange={toggleIntegration}/>
        </SortHeaderFilter>
    );
};

export const DealCloudIntegrationHeader = () => {
    const result = useSelector(accountSearchResultResultSelector);

    return result ? (
        <DealCloudIntegrationController
            accountIds={result.data.map(({accountId}) => accountId)}
            totalCount={result.totalCount}
        />
    ) : (
        <HeaderSkeleton columnId={COLUMNS.DEAL_CLOUD_INTEGRATION}/>
    );
};
